import {
    Button,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';
import React, {useState} from 'react';
import isValidURL from '../../lib/isValidURL.ts';

interface AddLinkModalProps {
    initialUrl?: string;
    initialTitle?: string;
    confirmLabel?: string;
    heading?: string;
    // Off for a linked picture, where the picture itself is the label.
    showTitle?: boolean;
    onClose: () => void;
    onLinkEntered: (url: string, title: string) => void;
}

/*
 * Collects the web address for a resource card or a linked picture. The url
 * is validated here so the author gets told straight away, but that is a
 * convenience only: the canvas re-validates before ever opening a link,
 * because its url can also arrive from a collaborator over SignalR.
 */
const AddLinkModal: React.FC<AddLinkModalProps> = ({
    initialUrl = '',
    initialTitle = '',
    confirmLabel = 'Add',
    heading = 'Add a link',
    showTitle = true,
    onClose,
    onLinkEntered
}) => {
    const [url, setUrl] = useState(initialUrl);
    const [title, setTitle] = useState(initialTitle);
    const [touched, setTouched] = useState(false);

    const trimmedUrl = url.trim();
    const isValid = isValidURL(trimmedUrl);
    const showError = touched && trimmedUrl.length > 0 && !isValid;

    const onConfirm = () => {
        setTouched(true);

        if (!isValid) {
            return;
        }

        onLinkEntered(trimmedUrl, title.trim());
    };

    return (
        <Modal isOpen toggle={onClose}>
            <ModalHeader>{heading}</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label for="ResourceCardUrl">Web address</Label>
                    <Input
                        id="ResourceCardUrl"
                        type="url"
                        autoFocus
                        placeholder="https://example.com/page"
                        value={url}
                        invalid={showError}
                        onBlur={() => setTouched(true)}
                        onChange={event => setUrl(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                onConfirm();
                            }
                        }}
                    />
                    {showError && (
                        <div className="invalid-feedback d-block">
                            Enter a full web address starting with http:// or
                            https://
                        </div>
                    )}
                </FormGroup>
                {showTitle && (
                    <FormGroup>
                        <Label for="ResourceCardTitle">Label (optional)</Label>
                        <Input
                            id="ResourceCardTitle"
                            type="text"
                            placeholder="What this link is"
                            value={title}
                            maxLength={120}
                            onChange={event => setTitle(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    onConfirm();
                                }
                            }}
                        />
                    </FormGroup>
                )}
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between align-items-center">
                <Button color="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button color="primary" disabled={!isValid} onClick={onConfirm}>
                    {confirmLabel}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddLinkModal;
